<div class="tailor-workspace">

  <!-- Create mode: intake absorbed (generate-on-arrival or picker-first) -->
  @if (mode === 'create') {
    @if (creating) {
      <ng-container [ngTemplateOutlet]="wsSkeletonTpl"
        [ngTemplateOutletContext]="{ caption: ('cv.tailor.workspace.generating' | translate) }"></ng-container>
    } @else if (createFailed) {
      <div class="ws-state">
        <nb-icon class="ws-state-icon" icon="alert-triangle-outline"></nb-icon>
        <p class="ws-state-label">{{ 'cv.tailor.workspace.createError' | translate }}</p>
        <button nbButton status="primary" type="button" (click)="retryCreate()">{{ 'cv.tailor.workspace.retry' | translate }}</button>
      </div>
    } @else if (pickerVisible) {
      <div class="ws-state landing">
        <div class="landing-card">
          <div class="landing-head">
            <span class="landing-ic"><nb-icon icon="flash-outline"></nb-icon></span>
            <div><div class="landing-t">{{ 'cv.tailor.landing.title' | translate }}</div>
                 <div class="landing-s">{{ 'cv.tailor.landing.sub' | translate }}</div></div>
          </div>
          <div class="landing-search">
            <nb-icon class="mag" icon="search-outline"></nb-icon>
            <input nbInput fullWidth [formControl]="landingQuery"
                   [placeholder]="'cv.tailor.intake.vacancySearchPlaceholder' | translate"
                   [nbSpinner]="landingSearching" nbSpinnerSize="tiny">
          </div>
          @for (v of landingResults; track v.id) {
            <div class="landing-row" (click)="onPickedVacancy(v)">
              <div class="landing-rmain"><span class="landing-rt">{{ v.title }}</span>
                <span class="landing-rm">{{ v.company }}@if (v.location) { &middot; {{ v.location }} }</span></div>
              @if (v.source) { <span class="landing-src">{{ v.source }}</span> }
            </div>
          }
          @if (landingDeals.length) {
            <div class="landing-group">{{ 'cv.tailor.landing.dealsGroup' | translate }}</div>
            @for (d of landingDeals; track d.id) {
              <div class="landing-row" (click)="onPickedDeal(d)">
                <div class="landing-rmain"><span class="landing-rt">{{ d.opportunity?.title }}</span>
                  <span class="landing-rm">{{ d.opportunity?.end_client?.name }} &middot; {{ ('crm.sub.' + d.status) | translate }}</span></div>
                <span class="landing-src">CRM</span>
              </div>
            }
          }
          @if (landingError && !landingSearching) {
            <p class="landing-err">{{ 'cv.tailor.intake.vacancySearchFailed' | translate }}</p>
          }
          <div class="landing-or">{{ 'cv.tailor.intake.orPaste' | translate }}</div>
          @if (!landingPasteOpen) {
            <button nbButton ghost size="small" status="primary" (click)="landingPasteOpen = true">
              <nb-icon icon="edit-2-outline"></nb-icon>{{ 'cv.tailor.landing.pasteCta' | translate }}
            </button>
          } @else {
            <textarea nbInput fullWidth rows="5" [(ngModel)]="pastedText"
                      [placeholder]="'cv.tailor.intake.pastePlaceholder' | translate"></textarea>
            <button nbButton status="primary" size="small" [disabled]="!pastedText.trim()" (click)="submitPaste()">
              {{ 'cv.tailor.landing.pasteSubmit' | translate }}
            </button>
          }
        </div>
      </div>
    }
  }

  <!-- Loading state -->
  @else if (loading) {
    <ng-container [ngTemplateOutlet]="wsSkeletonTpl"
      [ngTemplateOutletContext]="{ aria: ('cv.tailor.workspace.loading' | translate) }"></ng-container>
  }

  <!-- Error state -->
  @else if (loadFailed) {
    <div class="ws-state">
      <nb-icon class="ws-state-icon" icon="alert-triangle-outline"></nb-icon>
      <p class="ws-state-label">{{ 'cv.tailor.workspace.loadError' | translate }}</p>
      <button nbButton status="primary" type="button" (click)="load()">
        {{ 'cv.tailor.workspace.retry' | translate }}
      </button>
    </div>
  }

  @else if (pkg) {

    <!-- Package header: back-to-list + the target vacancy, shown for both artifact tabs -->
    <header class="ws-header">
      <button nbButton ghost status="basic" size="small" type="button" class="ws-back"
              (click)="backToList()"
              [attr.aria-label]="'cv.tailor.workspace.backToList' | translate">
        <nb-icon icon="arrow-back-outline"></nb-icon>
        <span class="ws-back-label">{{ 'cv.tailor.workspace.backToList' | translate }}</span>
      </button>
      <span class="ws-vacancy" [title]="vacancyHeading">{{ vacancyHeading }}</span>
      <!-- CRM linkage, shown for both artifact tabs. Factual, no action: the
           note explains any outcome other than linked. -->
      @if (crmSyncState) {
        <span class="crm-chip" [class.is-linked]="crmSyncState === 'linked'" [title]="crmSyncNote ?? ''">
          {{ ('cv.tailor.crm.' + crmSyncState) | translate }}
        </span>
      }
    </header>

    <!-- Before any artifact has a usable draft, render the generating/failed
         status as a full pane OUTSIDE the tabset. nb-tabset can leave its active
         tab's content unrendered during the create -> navigate -> poll re-entry
         (active-tab race), which surfaced as a blank white workspace mid-
         generation once the initial "generating" spinner cleared. -->
    @if (!hasCv && !hasLetter) {
      @if ((cvFailed || letterFailed) && !anyGenerating) {
        <div class="ws-state">
          <nb-icon class="ws-state-icon" icon="alert-triangle-outline"></nb-icon>
          <p class="ws-state-label">{{ 'cv.tailor.workspace.artifactFailed' | translate }}</p>
          <button nbButton status="primary" type="button" (click)="retryGenerate()">
            {{ 'cv.tailor.workspace.retryGenerate' | translate }}
          </button>
        </div>
      } @else {
        <!-- Generating, or the (unreachable) both-'none' race: a neutral skeleton.
             Failed is shown only when an artifact actually failed, so a non-failed
             "nothing ready yet" state never reads as an error and is never blank. -->
        <ng-container [ngTemplateOutlet]="wsSkeletonTpl"
          [ngTemplateOutletContext]="{ caption: ('cv.tailor.workspace.artifactGenerating' | translate) }"></ng-container>
      }
    } @else {

    <!-- Two-pane body: artifact tabs on the left, the shared refine chat right -->
    <div class="ws-panes">

      <section class="ws-left">
        <nb-tabset class="ws-tabset" (changeTab)="onTabChange($event)">

          <!-- ============================ CV tab ============================ -->
          <nb-tab tabId="cv"
                  [active]="cvTabActive"
                  [disabled]="!cvHasAny">
            <ng-template nbTabTitle>
              <span class="tab-title">
                {{ 'cv.tailor.workspace.tabCv' | translate }}
                @if (cvBadgeText) {
                  <span class="tab-status" [attr.data-status]="cvBadgeStatus">
                    <span class="tab-status-dot"></span>{{ cvBadgeText }}
                  </span>
                }
              </span>
            </ng-template>

            @if (cvGenerating) {
              <ng-container [ngTemplateOutlet]="docSkeletonTpl"
                [ngTemplateOutletContext]="{ caption: ('cv.tailor.workspace.artifactGenerating' | translate) }"></ng-container>
            } @else if (cvFailed) {
              <div class="artifact-pending artifact-failed">
                <nb-icon class="artifact-pending-icon" icon="alert-triangle-outline" status="danger"></nb-icon>
                <p class="artifact-pending-text">{{ 'cv.tailor.workspace.artifactFailed' | translate }}</p>
                <button nbButton status="primary" size="small" type="button" (click)="retryGenerate()">
                  <nb-icon icon="refresh-outline"></nb-icon>
                  {{ 'cv.tailor.workspace.retryGenerate' | translate }}
                </button>
              </div>
            } @else {

<header class="approve-bar">
  <div class="approve-status">
    <span class="status-chip" [class.is-final]="cvFinalized">
      {{ (cvFinalized ? 'cv.tailor.workspace.statusFinal' : 'cv.tailor.workspace.statusDraft') | translate }}
    </span>
    @if (coverage.total > 0) {
      <span class="hdr-meter" [class.is-loading]="turnInFlight"
            [attr.aria-busy]="turnInFlight ? true : null"
            [attr.aria-label]="'cv.tailor.workspace.coverageLabel' | translate: { matched: coverage.matched, total: coverage.total }">
        <span class="hdr-meter-label">{{ coverage.matched }} / {{ coverage.total }}</span>
        <nb-progress-bar class="hdr-meter-bar" [value]="coveragePct" [status]="coverageStatus" size="tiny"></nb-progress-bar>
      </span>
    } @else {
      <span class="approve-coverage">{{ 'cv.tailor.workspace.noRequirements' | translate }}</span>
    }
    @if (quota) {
      <span class="approve-coverage"
            [attr.title]="'cv.tailor.workspace.quotaLeft' | translate: { remaining: quota.remaining, cap: quota.cap }"
            [attr.aria-label]="'cv.tailor.workspace.quotaLeft' | translate: { remaining: quota.remaining, cap: quota.cap }">
        {{ 'cv.tailor.workspace.quotaLeft' | translate: { remaining: quota.remaining, cap: quota.cap } }}
      </span>
    }
  </div>

  <div class="approve-actions">
    @if (canRelease) {
      @if (cvFinalized) {
        <a nbButton status="primary" size="small" [href]="downloadUrl('cv')" target="_blank" rel="noopener">
          <nb-icon icon="download-outline"></nb-icon>
          {{ 'cv.tailor.workspace.downloadCv' | translate }}
        </a>
      } @else {
        <button nbButton status="primary" size="small" type="button" [disabled]="finalizing"
                [nbSpinner]="finalizing" nbSpinnerStatus="control" (click)="finalize('cv')">
          <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
          {{ 'cv.tailor.workspace.approve' | translate }}
        </button>
      }
    } @else if (canSubmit) {
      <button nbButton status="primary" size="small" type="button"
              [disabled]="isLocked || submitting" [nbSpinner]="submitting" nbSpinnerStatus="control"
              (click)="submit()">
        <nb-icon icon="paper-plane-outline"></nb-icon>
        {{ (pkg?.submitted_at ? 'cv.tailor.submit.submitted' : 'cv.tailor.submit.action') | translate }}
      </button>
    }
    <button nbButton ghost status="basic" size="small" type="button" class="overflow-btn"
            [nbPopover]="cvOverflowMenu" nbPopoverTrigger="click" nbPopoverPlacement="bottom"
            #cvOverflow="nbPopover" [attr.aria-label]="'cv.tailor.workspace.moreActions' | translate">
      <nb-icon icon="more-vertical-outline"></nb-icon>
    </button>
    <ng-template #cvOverflowMenu>
      <div class="kebab-popup">
        <button class="kebab-item" (click)="drawerOpen = !drawerOpen; closeOverflow('cv')">
          <nb-icon icon="settings-2-outline"></nb-icon>{{ 'cv.tailor.settings.title' | translate }}
        </button>
        @if (cvFinalized) {
          <button class="kebab-item" [disabled]="promoting" (click)="promoteExemplar('cv'); closeOverflow('cv')">
            <nb-icon icon="bookmark-outline"></nb-icon>{{ 'cv.tailor.workspace.promoteExemplar' | translate }}
          </button>
          @if (canRelease) {
            <button class="kebab-item" [disabled]="finalizing" (click)="finalize('cv'); closeOverflow('cv')">
              <nb-icon icon="refresh-outline"></nb-icon>{{ 'cv.tailor.workspace.regenerate' | translate }}
            </button>
          }
        }
      </div>
    </ng-template>
  </div>
</header>

            @if (drawerOpen) {
              <div class="settings-drawer">
                <header class="settings-drawer-header">
                  <h2>{{ 'cv.tailor.settings.title' | translate }}</h2>
                  <button nbButton ghost size="small" status="basic" type="button" (click)="drawerOpen = false">
                    <nb-icon icon="close-outline"></nb-icon>{{ 'cv.tailor.intake.cancel' | translate }}
                  </button>
                </header>
                <bst-tailor-intake
                  mode="settings"
                  [busy]="inclusionBusy"
                  [disabled]="isLocked"
                  [userId]="pkg.subject_user_id"
                  [clientId]="pkg.client_id"
                  [templates]="templates"
                  [templateId]="pkg.cv_template_id"
                  [locale]="pkg.language"
                  [vacancyId]="pkg.vacancy_id"
                  [templateType]="pkg.cv_template_type"
                  [clientCvTemplateId]="pkg.client_cv_template_id ?? null"
                  [claimStrength]="$any(pkg.intake?.claim_strength) || 'soft'"
                  [availabilityValue]="pkg.intake?.availability || ''"
                  [salutationValue]="pkg.intake?.salutation || ''"
                  (regenerate)="onRegenerate($event)"
                  (templateChanged)="onTemplateChange($event)"
                  (cancelled)="drawerOpen = false">
                </bst-tailor-intake>
              </div>
            } @else {
            <div class="left-toolbar">
              <div class="seg-switch" role="radiogroup">
                <button type="button" role="radio" class="seg-item" [class.is-active]="cvView === 'preview'"
                        [attr.aria-checked]="cvView === 'preview'" (click)="cvView = 'preview'">{{ 'cv.tailor.workspace.viewPreview' | translate }}</button>
                <button type="button" role="radio" class="seg-item" [class.is-active]="cvView === 'changes'"
                        [attr.aria-checked]="cvView === 'changes'" (click)="cvView = 'changes'">{{ 'cv.tailor.workspace.viewChanges' | translate }}</button>
                @if (hasCvProposal) {
                  <!-- Only while a proposal is open, and alongside Changes
                       rather than instead of it: the two answer different
                       questions and the user needs both to judge an accept. -->
                  <button type="button" role="radio" class="seg-item is-proposal" [class.is-active]="cvView === 'proposal'"
                          [attr.aria-checked]="cvView === 'proposal'" (click)="cvView = 'proposal'">{{ 'cv.tailor.workspace.viewProposal' | translate }}</button>
                }
                <button type="button" role="radio" class="seg-item" [class.is-active]="cvView === 'experiences'"
                        [attr.aria-checked]="cvView === 'experiences'" (click)="cvView = 'experiences'">{{ 'cv.tailor.workspace.viewInclude' | translate }}</button>
              </div>
            </div>

            <!-- Preview/Changes/Include and the coverage board below it share one
                 scroll owner. They used to be flex siblings competing for the tab's
                 fixed height directly, so a tall coverage board (many missing chips)
                 squeezed the CV preview instead of the pane just scrolling, and the
                 two bordered cards ended up flush against each other with no gap. -->
            <div class="cv-pane-scroll">
            <div class="left-view">

              <!-- Changes (cumulative, readable diff) and Proposal (this turn's
                   pending before->after) share the row markup but never the
                   source: reviewDiff keys off cvView, so each view answers only
                   its own question. -->
              @if (cvView === 'changes' || cvView === 'proposal') {
                <div class="diff-view">
                  @if (canManualEdit) {
                    <div class="header-edit-row">
                      <button nbButton ghost size="tiny" status="basic" type="button"
                              (click)="selectField({ kind: 'cv', field: 'position' })">
                        {{ 'cv.tailor.edit.editPosition' | translate }}
                      </button>
                      <button nbButton ghost size="tiny" status="basic" type="button"
                              (click)="selectField({ kind: 'cv', field: 'profile_text' })">
                        {{ 'cv.tailor.edit.editProfile' | translate }}
                      </button>
                      <button nbButton ghost size="tiny" status="basic" type="button"
                              (click)="selectField({ kind: 'cv', field: 'contact', contactField: 'phone' })">
                        {{ 'cv.tailor.edit.editPhone' | translate }}
                      </button>
                      <button nbButton ghost size="tiny" status="basic" type="button"
                              (click)="selectField({ kind: 'cv', field: 'contact', contactField: 'email' })">
                        {{ 'cv.tailor.edit.editEmail' | translate }}
                      </button>
                      <button nbButton ghost size="tiny" status="basic" type="button"
                              (click)="selectField({ kind: 'cv', field: 'contact', contactField: 'linkedin_url' })">
                        {{ 'cv.tailor.edit.editLinkedin' | translate }}
                      </button>
                      <button nbButton ghost size="tiny" status="basic" type="button"
                              (click)="selectField({ kind: 'cv', field: 'contact', contactField: 'address' })">
                        {{ 'cv.tailor.edit.editAddress' | translate }}
                      </button>
                    </div>
                  }
                  @if (cvView === 'proposal') {
                    <p class="proposed-banner">
                      <nb-icon icon="alert-circle-outline"></nb-icon>
                      {{ 'cv.tailor.workspace.proposedNotAccepted' | translate }}
                    </p>
                  }
                  @if (cvView === 'proposal' && !proposedDiff) {
                    <!-- A proposal recorded before per-turn snapshotting has no
                         before->after to render. Say that, rather than falling
                         back to the cumulative diff, which would present already
                         applied edits as the thing awaiting a decision. -->
                    <p class="diff-empty">{{ 'cv.tailor.workspace.proposedNoDetail' | translate }}</p>
                  } @else if (!hasChanges) {
                    <p class="diff-empty">{{ 'cv.tailor.workspace.noChanges' | translate }}</p>
                  } @else {
                    <ul class="diff-rows">
                      @if (reviewDiff.header?.position) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffRole' | translate }}</span>
                          <span class="diff-detail">
                            @if (text(reviewDiff.header?.position?.from)) {
                              {{ 'cv.tailor.workspace.diffWas' | translate }}
                              <span class="diff-from">{{ text(reviewDiff.header?.position?.from) }}</span>,
                            }
                            {{ 'cv.tailor.workspace.diffNow' | translate }}
                            <span class="diff-to">{{ text(reviewDiff.header?.position?.to) }}</span>
                          </span>
                        </li>
                      }
                      @if (reviewDiff.header?.profile_text) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffProfile' | translate }}</span>
                          <!-- Stacked, not the role line's inline "was X, now Y": a
                               profile is a paragraph, and running the old text,
                               an arrow icon, and the new text together inline
                               read as one indistinguishable blob of text. -->
                          <div class="diff-detail-stacked">
                            @if (text(reviewDiff.header?.profile_text?.from)) {
                              <p class="diff-from">
                                <span class="diff-block-tag">{{ 'cv.tailor.workspace.diffWas' | translate }}</span>
                                {{ text(reviewDiff.header?.profile_text?.from) }}
                              </p>
                            }
                            <p class="diff-to">
                              <span class="diff-block-tag">{{ 'cv.tailor.workspace.diffNow' | translate }}</span>
                              {{ text(reviewDiff.header?.profile_text?.to) }}
                            </p>
                          </div>
                        </li>
                      }
                      @if (changedExperienceCount > 0) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffExperienceChanged' | translate }}</span>
                          <span class="diff-detail diff-to">{{ changedExperienceCount }}</span>
                        </li>
                      }
                      @if (reviewDiff.experience?.added?.length) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffExperienceAdded' | translate }}</span>
                          <span class="diff-detail diff-to">{{ reviewDiff.experience?.added?.length }}</span>
                        </li>
                      }
                      @if (reviewDiff.experience?.removed?.length) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffExperienceRemoved' | translate }}</span>
                          <span class="diff-detail diff-to">{{ reviewDiff.experience?.removed?.length }}</span>
                        </li>
                      }
                      @if (reviewDiff.experience?.reordered) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffExperienceReordered' | translate }}</span>
                          <span class="diff-detail diff-to">{{ 'cv.tailor.workspace.diffReordered' | translate }}</span>
                        </li>
                      }
                      @if (groupChanged(reviewDiff.skills)) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffSkills' | translate }}</span>
                          <span class="diff-detail diff-to">
                            {{ 'cv.tailor.workspace.diffSkillsSummary' | translate: {
                              added: (reviewDiff.skills?.added?.length || 0),
                              removed: (reviewDiff.skills?.removed?.length || 0)
                            } }}
                            @if (reviewDiff.skills?.reordered) { , {{ 'cv.tailor.workspace.diffReordered' | translate }} }
                          </span>
                          @if (removedRows('skill').length) {
                            <div class="diff-removal-list">
                              @for (row of removedRows('skill'); track row.id) {
                                <span class="diff-removal">
                                  <span class="diff-label-chip removed">{{ row.label }}</span>
                                  @if (row.reason) {
                                    <span class="diff-removal-reason">{{ row.reason }}</span>
                                  }
                                </span>
                              }
                            </div>
                          }
                        </li>
                      }
                      @if (groupChanged(reviewDiff.soft_skills)) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffSoftSkills' | translate }}</span>
                          <span class="diff-detail diff-to">
                            {{ 'cv.tailor.workspace.diffSkillsSummary' | translate: {
                              added: (reviewDiff.soft_skills?.added?.length || 0),
                              removed: (reviewDiff.soft_skills?.removed?.length || 0)
                            } }}
                            @if (reviewDiff.soft_skills?.reordered) { , {{ 'cv.tailor.workspace.diffReordered' | translate }} }
                          </span>
                          @if (removedRows('soft_skill').length) {
                            <div class="diff-removal-list">
                              @for (row of removedRows('soft_skill'); track row.id) {
                                <span class="diff-removal">
                                  <span class="diff-label-chip removed">{{ row.label }}</span>
                                  @if (row.reason) {
                                    <span class="diff-removal-reason">{{ row.reason }}</span>
                                  }
                                </span>
                              }
                            </div>
                          }
                        </li>
                      }
                      @if (changedEducationCount > 0) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffEducationChanged' | translate }}</span>
                          <span class="diff-detail diff-to">{{ changedEducationCount }}</span>
                        </li>
                      }
                      @if (groupChanged(reviewDiff.education)) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffEducation' | translate }}</span>
                          <span class="diff-detail diff-to">
                            {{ 'cv.tailor.workspace.diffSkillsSummary' | translate: {
                              added: (reviewDiff.education?.added?.length || 0),
                              removed: (reviewDiff.education?.removed?.length || 0)
                            } }}
                            @if (reviewDiff.education?.reordered) { , {{ 'cv.tailor.workspace.diffReordered' | translate }} }
                          </span>
                        </li>
                      }
                      @if (groupChanged(reviewDiff.certifications)) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffCertifications' | translate }}</span>
                          <span class="diff-detail diff-to">
                            {{ 'cv.tailor.workspace.diffSkillsSummary' | translate: {
                              added: (reviewDiff.certifications?.added?.length || 0),
                              removed: (reviewDiff.certifications?.removed?.length || 0)
                            } }}
                            @if (reviewDiff.certifications?.reordered) { , {{ 'cv.tailor.workspace.diffReordered' | translate }} }
                          </span>
                        </li>
                      }
                      @if (groupChanged(reviewDiff.languages)) {
                        <li class="diff-row">
                          <span class="diff-label">{{ 'cv.tailor.workspace.diffLanguages' | translate }}</span>
                          <span class="diff-detail diff-to">
                            {{ 'cv.tailor.workspace.diffSkillsSummary' | translate: {
                              added: (reviewDiff.languages?.added?.length || 0),
                              removed: (reviewDiff.languages?.removed?.length || 0)
                            } }}
                            @if (reviewDiff.languages?.reordered) { , {{ 'cv.tailor.workspace.diffReordered' | translate }} }
                          </span>
                        </li>
                      }
                    </ul>
                  }
                </div>
              }

              <!-- Inclusion cockpit -->
              @else if (cvView === 'experiences') {
                <div class="incl-stack">
                <bst-experiences-panel
                  [experiences]="experiences"
                  [busy]="inclusionBusy || editBusy"
                  [disabled]="isLocked"
                  [manualEdit]="canManualEdit"
                  (restore)="onRestore($event)"
                  (setAside)="onSetAside($event)"
                  (editField)="selectField({ kind: 'cv', ...$event })"
                  (reorderRoles)="reorderExperiences($event)">
                </bst-experiences-panel>
                <bst-section-items-panel
                  [sections]="sectionInclusion"
                  [busy]="inclusionBusy || editBusy"
                  [disabled]="isLocked"
                  [manualEdit]="canManualEdit"
                  (setAside)="onSectionSetAside($event)"
                  (restore)="onSectionRestore($event)"
                  (reorderItems)="reorderSection($event.section, $event.order)">
                </bst-section-items-panel>
                <bst-education-panel
                  [education]="education"
                  [busy]="inclusionBusy || editBusy"
                  [disabled]="isLocked"
                  [manualEdit]="canManualEdit"
                  (setAside)="onEducationSetAside($event)"
                  (restore)="onEducationRestore($event)"
                  (descriptionChange)="onEducationDescription($event)"
                  (editDescription)="selectField({ kind: 'cv', field: 'education_description', educationId: $event.eduId, label: $event.label, current: $event.current })"
                  (editField)="selectField({ kind: 'cv', ...$event })">
                </bst-education-panel>
                </div>
              }

              <!-- CV preview -->
              @else {
                <div class="preview-frame-wrapper" [style.--cv-content-height]="previewContentHeight ? (previewContentHeight + 'px') : null">
                  <div class="preview-frame-stage" [style.max-height.px]="maxPreviewContentHeightPx">
                    @if (previewHtml) {
                      <iframe #previewIframe class="preview-iframe" [srcdoc]="previewHtml" [title]="'cv.tailor.workspace.title' | translate"
                              (load)="onPreviewIframeLoad($event)"></iframe>
                    }
                    @if (previewLoading) {
                      <div class="preview-skeleton" aria-hidden="true">
                        <span class="sk sk-title"></span>
                        <span class="sk sk-line"></span>
                        <span class="sk sk-line short"></span>
                        <span class="sk sk-gap"></span>
                        <span class="sk sk-line"></span>
                        <span class="sk sk-line"></span>
                        <span class="sk sk-line short"></span>
                      </div>
                    }
                    @if (previewFailed && !previewLoading) {
                      <div class="preview-error">
                        <nb-icon class="ws-state-icon" icon="alert-triangle-outline"></nb-icon>
                        <p class="ws-state-label">{{ 'cv.tailor.workspace.previewError' | translate }}</p>
                        <button nbButton status="primary" size="small" type="button" (click)="refreshPreview()">
                          {{ 'cv.tailor.workspace.retry' | translate }}
                        </button>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>

            <!-- Coverage detail (per-requirement breakdown, #705). The summary meter
                 lives in the header, so the board itself drops its own bar, but
                 always keeps a title: on its own, a bare list of skill rows and
                 "Missing:" chips gives no clue what it's showing.

                 Shown on Include only (PO decision 2026-09-15, #1440; PR #1359 had
                 it on Preview too). Its per-requirement detail (which requirements
                 are missing, which skills are kept BECAUSE they're required) is
                 decision-relevant exactly where inclusion is decided, and on
                 Preview it competed with the render for the pane. Changes and
                 Proposal answer "what changed", a different question. -->
            @if (coverage.total > 0 && cvView === 'experiences') {
              <div class="coverage-board" [attr.aria-busy]="turnInFlight ? true : null">
                <div class="coverage-board-head">
                  <span class="coverage-board-title">{{ 'cv.tailor.workspace.coverageBoardTitle' | translate }}</span>
                  <span class="coverage-board-sub">{{ 'cv.tailor.workspace.coverageBoardSub' | translate }}</span>
                </div>
                @if (turnInFlight) {
                  <!-- A turn is in flight; the response carries fresh coverage, so
                       skeleton the board instead of showing the stale numbers. -->
                  <div class="coverage-loading" role="status"
                       [attr.aria-label]="'cv.tailor.workspace.coverageLoading' | translate">
                    <div aria-hidden="true" class="sk-line"></div>
                    <div aria-hidden="true" class="sk-line sm"></div>
                    <div aria-hidden="true" class="sk-line"></div>
                    <div aria-hidden="true" class="sk-line sm"></div>
                  </div>
                } @else {
                @if (coverage.mode === 'legacy') {
                  <span class="coverage-mode">{{ 'cv.tailor.workspace.coverageLegacy' | translate }}</span>
                }
                @if (keptRequiredRows.length) {
                  <div class="coverage-kept">
                    <span class="coverage-missing-label">{{ 'cv.tailor.workspace.coverageKeptRequired' | translate }}</span>
                    @for (r of keptRequiredRows; track r.id) {
                      <span class="kept-chip">{{ rejectedLabel(r) }}</span>
                    }
                  </div>
                }
                @if (coverageRows.length) {
                  <div class="coverage-detail-list">
                    @for (row of coverageRows; track row.requirement) {
                      <div class="coverage-detail-row" [class.is-covered]="row.covered">
                        <span class="coverage-requirement">{{ row.requirement }}</span>
                        @if (row.covered) {
                          <span class="coverage-evidence">
                            {{ coverageSkillLabels(row) }}
                            @if (row.via) {
                              <span class="coverage-via">{{ row.via }}</span>
                            }
                          </span>
                        } @else {
                          <span class="coverage-evidence missing">{{ 'cv.tailor.workspace.coverageNotCovered' | translate }}</span>
                        }
                      </div>
                    }
                  </div>
                }
                @if (coverage.missing?.length) {
                  <div class="coverage-missing">
                    <span class="coverage-missing-label">{{ 'cv.tailor.workspace.coverageMissing' | translate }}</span>
                    @for (m of coverage.missing; track m) {
                      <span class="missing-chip">{{ m }}</span>
                    }
                  </div>
                }
                }
              </div>
            }
            </div>
            }
            }
          </nb-tab>

          <!-- ========================== Letter tab ========================= -->
          <nb-tab #letterTab tabId="letter"
                  [active]="letterTabActive"
                  [disabled]="!letterHasAny">
            <ng-template nbTabTitle>
              <span class="tab-title">
                {{ 'cv.tailor.workspace.tabLetter' | translate }}
                @if (letterBadgeText) {
                  <span class="tab-status" [attr.data-status]="letterBadgeStatus">
                    <span class="tab-status-dot"></span>{{ letterBadgeText }}
                  </span>
                }
              </span>
            </ng-template>

            @if (letterGenerating) {
              <ng-container [ngTemplateOutlet]="docSkeletonTpl"
                [ngTemplateOutletContext]="{ caption: ('cv.tailor.workspace.artifactGenerating' | translate) }"></ng-container>
            } @else if (letterFailed) {
              <div class="artifact-pending artifact-failed">
                <nb-icon class="artifact-pending-icon" icon="alert-triangle-outline" status="danger"></nb-icon>
                <p class="artifact-pending-text">{{ 'cv.tailor.workspace.artifactFailed' | translate }}</p>
                <button nbButton status="primary" size="small" type="button" (click)="retryGenerate()">
                  <nb-icon icon="refresh-outline"></nb-icon>
                  {{ 'cv.tailor.workspace.retryGenerate' | translate }}
                </button>
              </div>
            } @else {

<header class="approve-bar">
  <div class="approve-status">
    <span class="status-chip" [class.is-final]="letterFinalized">
      {{ (letterFinalized ? 'cv.tailor.workspace.statusFinal' : 'cv.tailor.workspace.statusDraft') | translate }}
    </span>
  </div>

  <div class="approve-actions">
    @if (canRelease) {
      @if (letterFinalized) {
        <a nbButton status="primary" size="small" [href]="downloadUrl('letter')" target="_blank" rel="noopener">
          <nb-icon icon="download-outline"></nb-icon>
          {{ 'cv.tailor.workspace.downloadLetter' | translate }}
        </a>
        <button nbButton ghost status="basic" size="small" type="button" class="overflow-btn"
                [nbPopover]="letterOverflowMenu" nbPopoverTrigger="click" nbPopoverPlacement="bottom"
                #letterOverflow="nbPopover" [attr.aria-label]="'cv.tailor.workspace.moreActions' | translate">
          <nb-icon icon="more-vertical-outline"></nb-icon>
        </button>
        <ng-template #letterOverflowMenu>
          <div class="kebab-popup">
            <button class="kebab-item" [disabled]="promoting" (click)="promoteExemplar('letter'); closeOverflow('letter')">
              <nb-icon icon="bookmark-outline"></nb-icon>{{ 'cv.tailor.workspace.promoteExemplar' | translate }}
            </button>
            <button class="kebab-item" [disabled]="finalizing" (click)="finalize('letter'); closeOverflow('letter')">
              <nb-icon icon="refresh-outline"></nb-icon>{{ 'cv.tailor.workspace.regenerate' | translate }}
            </button>
          </div>
        </ng-template>
      } @else {
        <button nbButton status="primary" size="small" type="button" [disabled]="finalizing"
                [nbSpinner]="finalizing" nbSpinnerStatus="control" (click)="finalize('letter')">
          <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
          {{ 'cv.tailor.workspace.approveLetter' | translate }}
        </button>
      }
    } @else if (canSubmit) {
      <button nbButton status="primary" size="small" type="button"
              [disabled]="isLocked || submitting" [nbSpinner]="submitting" nbSpinnerStatus="control"
              (click)="submit()">
        <nb-icon icon="paper-plane-outline"></nb-icon>
        {{ (pkg?.submitted_at ? 'cv.tailor.submit.submitted' : 'cv.tailor.submit.action') | translate }}
      </button>
    }
  </div>
</header>

            <!-- Preview is the templated letter (GET .../preview/letter, the
                 same blade + tenant template finalize renders to PDF) in an
                 iframe, like the CV tab. Edit is the paragraph card with the
                 drag handles and Edit buttons, so it exists only while manual
                 editing is on; without it the card would only repeat the
                 preview as bare text. -->
            @if (pkg.letter_document && canManualEdit) {
              <div class="left-toolbar">
                <div class="seg-switch" role="radiogroup">
                  <button type="button" role="radio" class="seg-item" [class.is-active]="letterView === 'preview'"
                          [attr.aria-checked]="letterView === 'preview'" (click)="letterView = 'preview'">{{ 'cv.tailor.workspace.viewPreview' | translate }}</button>
                  <button type="button" role="radio" class="seg-item" [class.is-active]="letterView === 'edit'"
                          [attr.aria-checked]="letterView === 'edit'" (click)="letterView = 'edit'">{{ 'cv.tailor.workspace.viewEdit' | translate }}</button>
                </div>
              </div>
            }
            <div class="left-view">
              @if (pkg.letter_document || proposedLetter) {
                <!-- One bounded scroller around both cards, never one each: two
                     sibling scrollers split the fixed pane height and the letter
                     under review ends up readable a few lines at a time. -->
                <div class="letter-stack">
                  @if (proposedLetter) {
                    <!-- A letter proposal has no op-level diff, so the two versions
                         are shown in full: proposed first, because that is the one
                         being judged, then the live letter it would replace. -->
                    <div class="letter-preview letter-proposed">
                      <p class="proposed-banner">
                        <nb-icon icon="alert-circle-outline"></nb-icon>
                        {{ 'cv.tailor.workspace.proposedNotAccepted' | translate }}
                      </p>
                      <p class="letter-salutation">{{ proposedLetter.salutation }}</p>
                      @for (p of proposedLetterParagraphs; track $index) {
                        <p class="letter-paragraph">{{ p }}</p>
                      }
                      @if (proposedLetter.availability) {
                        <p class="letter-availability">{{ proposedLetter.availability }}</p>
                      }
                      @if (proposedLetter.signature) {
                        <p class="letter-signature">{{ proposedLetter.signature }}</p>
                      }
                    </div>
                  }
                  <!-- Mounted only while the letter tab shows: Safari resolves
                       --cv-scale's cqw against the hidden tab and never
                       re-resolves the transform on show, drawing the letter
                       too small inside a correctly sized sheet. Gated on the
                       tab Nebular shows, not activeTab: [active] flips do not
                       emit changeTab, so activeTab can lag. -->
                  @if (letterTab.active && pkg.letter_document && (letterView === 'preview' || !canManualEdit)) {
                    <div class="letter-frame">
                      @if (proposedLetter) {
                        <p class="letter-version-label">{{ 'cv.tailor.workspace.letterCurrent' | translate }}</p>
                      }
                      <!-- Same frame as the CV preview (classes and scale), so
                           the two cannot drift: a fixed 794px canvas,
                           CSS-scaled to fit the pane. -->
                      <div class="preview-frame-wrapper" [style.--cv-content-height]="letterPreviewContentHeight ? (letterPreviewContentHeight + 'px') : null">
                        <div class="preview-frame-stage" [style.max-height.px]="maxPreviewContentHeightPx">
                          @if (letterPreviewHtml) {
                            <iframe #letterPreviewIframe class="preview-iframe" [srcdoc]="letterPreviewHtml" [title]="'cv.tailor.workspace.tabLetter' | translate"
                                    (load)="onLetterPreviewIframeLoad($event)"></iframe>
                          }
                          @if (letterPreviewLoading) {
                            <div class="preview-skeleton" aria-hidden="true">
                              <span class="sk sk-title"></span>
                              <span class="sk sk-line"></span>
                              <span class="sk sk-line short"></span>
                              <span class="sk sk-gap"></span>
                              <span class="sk sk-line"></span>
                              <span class="sk sk-line"></span>
                              <span class="sk sk-line short"></span>
                            </div>
                          }
                          @if (letterPreviewFailed && !letterPreviewLoading) {
                            <div class="preview-error">
                              <nb-icon class="ws-state-icon" icon="alert-triangle-outline"></nb-icon>
                              <p class="ws-state-label">{{ 'cv.tailor.workspace.previewError' | translate }}</p>
                              <button nbButton status="primary" size="small" type="button" (click)="refreshLetterPreview()">
                                {{ 'cv.tailor.workspace.retry' | translate }}
                              </button>
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  }
                  @if (pkg.letter_document && canManualEdit && letterView === 'edit') {
                    <div class="letter-preview">
                      @if (proposedLetter) {
                        <p class="letter-version-label">{{ 'cv.tailor.workspace.letterCurrent' | translate }}</p>
                      }
                      <p class="letter-salutation">{{ pkg.letter_document.salutation }}</p>
                      <!-- This card exists only while manual editing is on (the
                           outer @if), so the drag handle and Edit button need
                           no guard of their own. Locked while a write is in
                           flight for the same reason the three cockpit panels
                           are: onLetterDrop() posts pkg.letter_fingerprint, so
                           a second drag before the first response carries a
                           stale one and 409s. -->
                      <div cdkDropList [cdkDropListDisabled]="inclusionBusy || editBusy"
                           [cdkDropListLockAxis]="'y'" (cdkDropListDropped)="onLetterDrop($event)">
                        @for (row of letterParagraphRows; track row.key) {
                          <div class="letter-paragraph-row" cdkDrag [cdkDragLockAxis]="'y'"
                               [cdkDragDisabled]="inclusionBusy || editBusy">
                            <span class="letter-drag-handle" cdkDragHandle><nb-icon icon="menu-2-outline"></nb-icon></span>
                            <!-- Click-to-edit (#1440): the paragraph opens the same editor
                                 the button does. cdkDrag starts only from the handle, so a
                                 click here never begins a drag. -->
                            <p class="letter-paragraph is-editable"
                               (click)="selectField({ kind: 'letter', field: 'paragraph', index: row.key, label: '', current: row.text })">{{ row.text }}</p>
                            <!-- The label is resolved by editorLabel(), NOT translated here:
                                 Angular rejects a pipe anywhere in an action expression
                                 (NG5002), and an unbracketed one silently reparsed the rest
                                 of this object literal as pipe arguments. -->
                            <button nbButton ghost size="tiny" status="basic" type="button"
                                    (click)="selectField({ kind: 'letter', field: 'paragraph', index: row.key, label: '', current: row.text })">
                              {{ 'cv.tailor.edit.editParagraph' | translate }}
                            </button>
                          </div>
                        }
                      </div>
                      @if (pkg.letter_document.availability) {
                        <p class="letter-availability">{{ pkg.letter_document.availability }}</p>
                      }
                      <div class="letter-signature-row">
                        <p class="letter-signature is-editable"
                           (click)="selectField({ kind: 'letter', field: 'signature', label: '', current: pkg.letter_document.signature })">{{ pkg.letter_document.signature || ('cv.tailor.edit.fieldSignature' | translate) }}</p>
                        <button nbButton ghost size="tiny" status="basic" type="button"
                                (click)="selectField({ kind: 'letter', field: 'signature', label: '', current: pkg.letter_document.signature })">
                          {{ 'cv.tailor.edit.editSignature' | translate }}
                        </button>
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div class="brief-placeholder">
                  <nb-icon class="brief-placeholder-icon" icon="email-outline"></nb-icon>
                  <p>{{ 'cv.tailor.workspace.briefPlaceholder' | translate }}</p>
                </div>
              }
            </div>
            }
          </nb-tab>

        </nb-tabset>
      </section>

      <!-- Right: refine chat. One kind-fixed instance per artifact, BOTH kept
           alive in the DOM and toggled with [hidden], so each renders only its
           own single-kind history + its own pending proposal (never a mix), and
           in-session turns survive a tab switch. Each mounts once its artifact
           has a usable draft; the unavailable notice covers the active tab
           whose artifact is still generating/failed. -->
      <aside class="ws-right">
        @if (selection) {
          @if (selection.field === 'period') {
            <bst-period-editor
              [label]="editorLabel"
              [start]="periodStart"
              [end]="periodEnd"
              [busy]="editBusy"
              (save)="savePeriod($event)"
              (cancel)="closeEditor()"
              (dirtyChange)="selectionDirty = $event">
            </bst-period-editor>
          } @else {
            <bst-field-editor
              [label]="editorLabel"
              [text]="editorText"
              [maxLength]="editorMaxLength"
              [busy]="editBusy"
              (save)="saveEdit($event)"
              (cancel)="closeEditor()"
              (dirtyChange)="selectionDirty = $event">
            </bst-field-editor>
          }
        }
        @if (cvArtifactReady) {
          <bst-tailor-chat
            [hidden]="activeTab !== 'cv'"
            [packageId]="pkg.id"
            [language]="pkg.language"
            [kind]="'cv'"
            [disabled]="isLocked"
            [turnCount]="cvTurnCount"
            [conversation]="cvConversation"
            [pendingProposal]="pendingProposal?.cv ?? null"
            (turnApplied)="onTurn($event)"
            (busyChange)="cvChatBusy = $event"
            (viewChanges)="cvView = 'changes'"
            (viewProposal)="cvView = 'proposal'">
          </bst-tailor-chat>
        }
        @if (letterArtifactReady) {
          <bst-tailor-chat
            [hidden]="activeTab !== 'letter'"
            [packageId]="pkg.id"
            [language]="pkg.language"
            [kind]="'letter'"
            [disabled]="isLocked"
            [turnCount]="letterTurnCount"
            [conversation]="letterConversation"
            [pendingProposal]="pendingProposal?.letter ?? null"
            (turnApplied)="onTurn($event)"
            (busyChange)="letterChatBusy = $event"
            (viewChanges)="cvView = 'changes'">
          </bst-tailor-chat>
        }
        @if (!activeArtifactReady) {
          <div class="chat-unavailable">
            <nb-icon class="chat-unavailable-icon" icon="message-circle-outline"></nb-icon>
            <p>{{ 'cv.tailor.workspace.refineUnavailable' | translate }}</p>
          </div>
        }
      </aside>
    </div>
    }
  }

  <!-- Reusable loading skeletons (shared sweep shimmer). `caption` shows a
       visible AI-generating label (announced via role=status); `aria` sets an
       sr-only label for a plain load with no visible caption. -->
  <ng-template #wsSkeletonTpl let-aria="aria" let-caption="caption">
    <div class="ws-skeleton" role="status"
         [attr.aria-label]="caption ? null : aria">
      <div class="sk-header" aria-hidden="true"></div>
      <div class="sk-panes" aria-hidden="true">
        <div class="sk-col sk-left">
          <div class="sk-line lg"></div>
          <div class="sk-line"></div>
          <div class="sk-line"></div>
          <div class="sk-line sm"></div>
          <div class="sk-block"></div>
        </div>
        <div class="sk-col sk-right">
          <div class="sk-line"></div>
          <div class="sk-line sm"></div>
          <div class="sk-block tall"></div>
        </div>
      </div>
      @if (caption) { <span class="sk-caption">{{ caption }}</span> }
    </div>
  </ng-template>

  <ng-template #docSkeletonTpl let-aria="aria" let-caption="caption">
    <div class="doc-skeleton" role="status"
         [attr.aria-label]="caption ? null : aria">
      <div aria-hidden="true" class="sk-line lg"></div>
      <div aria-hidden="true" class="sk-line"></div>
      <div aria-hidden="true" class="sk-line"></div>
      <div aria-hidden="true" class="sk-line sm"></div>
      <div aria-hidden="true" class="sk-block"></div>
      <div aria-hidden="true" class="sk-line"></div>
      <div aria-hidden="true" class="sk-line sm"></div>
      @if (caption) { <span class="sk-caption">{{ caption }}</span> }
    </div>
  </ng-template>
</div>
